import { endOfDay, startOfDay, subDays } from 'date-fns';

import type { FilterByOpts, SortByOpts } from '@core/api';
import Enums from '@core/types/api/Enums';
import type { NotificationMessage } from '@core/types/api/NotificationMessage/NotificationMessage';
import type { ReminderUserSettingSummary } from '@core/types/api/ReminderUserSettingSummary/ReminderUserSettingSummary';
import { hasStringValue, hasValue } from '@core/utils/equality';
import type {
  NotificationScope,
  NotificationScopeObject,
  NotificationTab,
} from '@core/utils/notifications';

export type NotificationFilter = FilterByOpts<
  NotificationMessage & { quoteId: string; projectId: string; activityId: string }
>;

export type ReminderFilter = FilterByOpts<ReminderUserSettingSummary & { recordId: string }>;

export type NotificationSort = SortByOpts<NotificationMessage & { priority: boolean }>;

export type ReminderSort = SortByOpts<ReminderUserSettingSummary>;

export enum NotificationSortOption {
  NewestFirst = 'newestFirst',
  OldestFirst = 'oldestFirst',
  Priority = 'priority',
  DueSoonest = 'dueSoonest',
  DueLatest = 'dueLatest',
}

export const NOTIFICATION_SORT_LABELS: Record<NotificationSortOption, string> = {
  [NotificationSortOption.NewestFirst]: 'Newest First',
  [NotificationSortOption.OldestFirst]: 'Oldest First',
  [NotificationSortOption.Priority]: 'Priority',
  [NotificationSortOption.DueSoonest]: 'Due Soonest',
  [NotificationSortOption.DueLatest]: 'Due Latest',
};

// First option of each tab is its default.
export const TAB_SORT_OPTIONS: Record<NotificationTab, NotificationSortOption[]> = {
  unread: [NotificationSortOption.NewestFirst, NotificationSortOption.OldestFirst],
  inbox: [NotificationSortOption.Priority, NotificationSortOption.NewestFirst],
  scheduled: [NotificationSortOption.DueSoonest, NotificationSortOption.DueLatest],
};

export const DEFAULT_NOTIFICATION_SORT: Record<NotificationTab, NotificationSortOption> = {
  unread: NotificationSortOption.NewestFirst,
  inbox: NotificationSortOption.Priority,
  scheduled: NotificationSortOption.DueSoonest,
};

export type NotificationFilterValues = {
  recordTypes: Enums.ReminderUserSettingsRecordType[];
  lastDays: number | null;
};

export const EMPTY_NOTIFICATION_FILTER_VALUES: NotificationFilterValues = {
  recordTypes: [],
  lastDays: null,
};

const SCOPE_OBJECT_FILTER_NAME: Record<
  NotificationScopeObject['type'],
  'quoteId' | 'projectId' | 'activityId'
> = {
  quote: 'quoteId',
  project: 'projectId',
  activity: 'activityId',
};

const SCOPE_OBJECT_RECORD_TYPE: Record<
  NotificationScopeObject['type'],
  Enums.ReminderUserSettingsRecordType
> = {
  quote: Enums.ReminderUserSettingsRecordType.Quote,
  project: Enums.ReminderUserSettingsRecordType.Project,
  activity: Enums.ReminderUserSettingsRecordType.Activity,
};

export const UNREAD_FILTER: NotificationFilter = {
  operation: Enums.FilterOperation.Equals,
  name: 'isRead',
  values: [false],
};

const NOT_ARCHIVED_FILTER: ReminderFilter = {
  operation: Enums.FilterOperation.Equals,
  name: 'isArchived',
  values: [false],
};

// Day boundaries keep the values, and so the query key, stable between renders.
const lastDaysRange = (days: number): [string, string] => {
  const today = new Date();
  return [startOfDay(subDays(today, days - 1)).toISOString(), endOfDay(today).toISOString()];
};

type NotificationFilterParams = {
  tab: NotificationTab;
  search: string;
  scope: NotificationScope;
  scopeObject: NotificationScopeObject | null;
  filterValues: NotificationFilterValues;
};

export const notificationFilters = ({
  tab,
  search,
  scope,
  scopeObject,
  filterValues,
}: NotificationFilterParams): NotificationFilter[] => {
  const filters: NotificationFilter[] = [];
  if (tab === 'unread') {
    filters.push(UNREAD_FILTER);
  }
  if (hasStringValue(search)) {
    filters.push({ operation: Enums.FilterOperation.Search, values: [search] });
  }
  if (filterValues.recordTypes.length > 0) {
    filters.push({
      operation: Enums.FilterOperation.In,
      name: 'recordType',
      values: filterValues.recordTypes,
    });
  }
  if (hasValue(filterValues.lastDays)) {
    filters.push({
      operation: Enums.FilterOperation.Between,
      name: 'createdAt',
      values: lastDaysRange(filterValues.lastDays),
    });
  }
  if (scope === 'currentObject' && hasValue(scopeObject)) {
    filters.push({
      operation: Enums.FilterOperation.Equals,
      name: SCOPE_OBJECT_FILTER_NAME[scopeObject.type],
      values: [scopeObject.id],
    });
  }
  return filters;
};

export const notificationSort = (option: NotificationSortOption): NotificationSort => {
  switch (option) {
    case NotificationSortOption.Priority:
      // The backend applies a fixed order and ignores the direction.
      return { name: 'priority', direction: Enums.SortDirection.Ascending };
    case NotificationSortOption.OldestFirst:
      return { name: 'createdAt', direction: Enums.SortDirection.Ascending };
    default:
      return { name: 'createdAt', direction: Enums.SortDirection.Descending };
  }
};

export const reminderSort = (option: NotificationSortOption): ReminderSort => ({
  name: 'triggersAt',
  direction:
    option === NotificationSortOption.DueLatest
      ? Enums.SortDirection.Descending
      : Enums.SortDirection.Ascending,
});

const recordFilters = (
  recordType: Enums.ReminderUserSettingsRecordType,
  recordId: string,
): ReminderFilter[] => [
  { operation: Enums.FilterOperation.Equals, name: 'recordType', values: [recordType] },
  { operation: Enums.FilterOperation.Equals, name: 'recordId', values: [recordId] },
];

export const recordReminderFilters = (
  recordType: Enums.ReminderUserSettingsRecordType,
  recordId: string,
): ReminderFilter[] => [NOT_ARCHIVED_FILTER, ...recordFilters(recordType, recordId)];

export const reminderFilters = ({
  search,
  scope,
  scopeObject,
  filterValues,
}: Omit<NotificationFilterParams, 'tab'>): ReminderFilter[] => {
  const filters: ReminderFilter[] = [NOT_ARCHIVED_FILTER];
  if (hasStringValue(search)) {
    filters.push({ operation: Enums.FilterOperation.Search, values: [search] });
  }
  if (filterValues.recordTypes.length > 0) {
    filters.push({
      operation: Enums.FilterOperation.In,
      name: 'recordType',
      values: filterValues.recordTypes,
    });
  }
  if (scope === 'currentObject' && hasValue(scopeObject)) {
    filters.push(...recordFilters(SCOPE_OBJECT_RECORD_TYPE[scopeObject.type], scopeObject.id));
  }
  return filters;
};
